import {
  QueryFunctionContext,
  UseInfiniteQueryOptions,
  UseQueryOptions
} from '@tanstack/react-query';
import { GetOrderHistoryQuery, GetThreePMReport, Summary } from './types';
import { axiosHelper } from '../../../axios-instance';
import {
  AxiosMethods,
  OrdersApi,
  AccountApi,
  SortType
} from '../../../../enums';
import { ReorderProducts, Order, Customer } from '../../../../interfaces';
import {
  formatCustomerArray,
  formatCustomerNames
} from '../../../../utils/formatCustomerData';

enum OrdersQueryKey {
  ORDER_HISTORY = 'orderHistory',
  CUSTOMERS = 'customers',
  CUSTOMER = 'customer',
  CREATE_ORDER = 'createOrder',
  SUBMIT_ORDER = 'submitOrder',
  ADDRESS = 'address',
  THREE_PM_REPORT = 'threePMReport'
}

const dynamicAccountNumberId = ':accountNumber';
const dynamicOrderId = ':orderId';
const dynamicCustomerId = ':customerId';
const dynamicAddressId = ':addressId';
interface GetOrderHistoryQueryParams {
  accountNumber: string;
  searchStr: string;
  limit: number;
  offset: number;
  sort?: {
    sortBy: string;
    orderBy: SortType;
  };
}

export const getOrderHistoryQuery = (
  params: GetOrderHistoryQueryParams
): UseInfiniteQueryOptions<GetOrderHistoryQuery> => ({
  queryKey: [OrdersQueryKey.ORDER_HISTORY, params],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: OrdersApi.orderHistory.replace(
        dynamicAccountNumberId,
        params.accountNumber
      ),
      method: AxiosMethods.POST,
      body: {
        searchStr: params.searchStr,
        limit: params.limit,
        offset: data.pageParam || params.offset,
        ...(params?.sort?.sortBy
          ? {
              sort: [
                {
                  field: params.sort.sortBy,
                  direction: params.sort.orderBy
                }
              ]
            }
          : {})
      }
    }),
  enabled: !!params.accountNumber,
  cacheTime: 0,
  staleTime: 0,
  getNextPageParam: (lastPage, allPages) => {
    const total = allPages?.reduce((acc, cur) => acc + cur?.data?.length, 0);
    if (total >= lastPage?.total) return undefined;
    return allPages.length * params?.limit;
  }
});

interface GetCustomersQueryParams {
  searchStr: string;
  limit: number;
  offset: number;
}

export const getCustomersQuery = (params: GetCustomersQueryParams) => ({
  queryKey: [OrdersQueryKey.CUSTOMERS, params],
  queryFn: async (data: QueryFunctionContext) => {
    const response = await axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: AccountApi.getAccounts,
      method: AxiosMethods.POST,
      body: {
        searchStr: params.searchStr,
        limit: params.limit,
        offset: data.pageParam || params.offset
      }
    });

    // Format customer names to title case when data arrives
    if (response?.data && Array.isArray(response.data)) {
      response.data = formatCustomerArray(response.data);
    }

    return response;
  },
  getNextPageParam: (lastPage: any, allPages: any) => {
    const total = allPages?.reduce(
      (acc: any, cur: any) => acc + cur?.data?.length,
      0
    );
    if (total >= lastPage?.total) return undefined;
    return allPages.length * params?.limit;
  }
});

interface GetCustomerByUniqueValue {
  uniqueValue: Customer['accountNumber'] | Customer['_id'];
}

/**
 * @param {string} props.uniqueValue - This value should be customer account number, id or some other unique value.
 */
export const getCustomerByUniqueValue = () => ({
  mutationFn: ({ uniqueValue }: GetCustomerByUniqueValue) =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: AccountApi.getAccounts,
      method: AxiosMethods.POST,
      body: {
        searchStr: uniqueValue,
        limit: 1,
        offset: 0
      }
    }).then(res => {
      const customer = res?.data?.[0];
      return customer ? formatCustomerNames(customer) : customer;
    })
});

interface GetAddressQueryParams {
  addressId: string;
}

export const getAddressQuery = ({ addressId }: GetAddressQueryParams) => ({
  queryKey: [OrdersQueryKey.ADDRESS, addressId],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: AccountApi.getAddresses.replace(dynamicAddressId, addressId),
      method: AxiosMethods.GET
    })
});
interface GetCustomerQueryParams {
  customerId: string;
}

export const getCustomerQuery = ({ customerId }: GetCustomerQueryParams) => ({
  queryKey: [OrdersQueryKey.CUSTOMER, customerId],
  queryFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: AccountApi.getCustomer.replace(dynamicCustomerId, customerId),
      method: AxiosMethods.GET
    })
});

export const downloadBackorderCSV = (accoundID: string) => ({
  queryKey: ['file_downloader', accoundID],
  queryFn: () =>
    axiosHelper({
      endpoint: `/order/backorders/csv/${accoundID}`,
      method: AxiosMethods.GET,
      baseURL: process.env.REVOPS_API
    }),
  enabled: false
});

type CreateOrderBody = {
  accountId: string;
  addressId: string;
  deliveryDate: string;
};

export const createOrder = () => ({
  mutationFn: (body: CreateOrderBody) =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: OrdersApi.create,
      method: AxiosMethods.POST,
      body
    })
});

interface SaveOrderItem {
  productId: string;
  quantity: number;
  sell: number;
  cost: number;
}

export const saveOrderItems = (orderId: string) => ({
  mutationFn: (products: SaveOrderItem[]) =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: OrdersApi.saveOrderItems.replace(dynamicOrderId, orderId),
      method: AxiosMethods.POST,
      body: products
    })
});

export const submitOrder = (orderId: string) => ({
  mutationFn: () =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: OrdersApi.submitOrder.replace(dynamicOrderId, orderId),
      method: AxiosMethods.POST
    })
});

export const updateOrder = () => ({
  mutationFn: (order: Order) => {
    const orderToUpdate = { ...order };
    if ('__v' in orderToUpdate) {
      // eslint-disable-next-line no-underscore-dangle
      delete (orderToUpdate as any).__v;
    }
    if (orderToUpdate.address && '__v' in orderToUpdate.address) {
      // eslint-disable-next-line no-underscore-dangle
      delete (orderToUpdate.address as any).__v;
    }

    return axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: OrdersApi.update,
      method: AxiosMethods.POST,
      body: orderToUpdate
    });
  }
});

interface CustomerReorderProductsResponse {
  products: ReorderProducts[];
  totalItems: number;
  isSuccess: boolean;
  isCancelled: boolean;
}
export interface CustomersReorderProps {
  customerId?: string;
  shipId?: string;
  search?: string;
  isOverdue?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  orderBy?: SortType;
  daysLateFilter?: number;
  selectedRep?: string;
  selectedTerritory?: string;
  PriorityLabel?: string;
  ConfidenceLabel?: string;
}

export const cleanParams = <T extends object>(obj: T) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  );

export const customersReorder = ({
  customerId = undefined,
  shipId = undefined,
  search = null,
  isOverdue = true,
  page = 1,
  pageSize = 20,
  sortBy,
  orderBy,
  daysLateFilter,
  selectedRep,
  selectedTerritory,
  PriorityLabel,
  ConfidenceLabel
}: CustomersReorderProps): UseInfiniteQueryOptions<CustomerReorderProductsResponse> => ({
  queryKey: [
    'customers-reorders',
    customerId,
    shipId,
    isOverdue,
    search,
    sortBy,
    orderBy,
    daysLateFilter,
    selectedRep,
    selectedTerritory,
    PriorityLabel,
    ConfidenceLabel
  ],
  queryFn: ({ pageParam, signal }) => {
    const controller = new AbortController();

    const promise = axiosHelper({
      endpoint: `/api/1.0/products/reorder/paginated`,
      params: cleanParams({
        CustomerId: customerId,
        ShipId: shipId,
        Search: search,
        IsOverdue: isOverdue.toString(),
        Page: pageParam || page,
        PageSize: pageSize.toString(),
        SortByAttribute: sortBy,
        SortByType: orderBy,
        DaysLateFilterMin: daysLateFilter,
        SalesRepId: selectedRep,
        TerritoryId: selectedTerritory,
        PriorityLabel,
        ConfidenceLabel
      }),
      method: AxiosMethods.GET,
      baseURL: process.env.INTELLIGENCE_API_BASE_URL,
      signal: controller.signal
    });

    signal?.addEventListener('abort', () => {
      controller.abort();
    });

    return promise;
  },
  getNextPageParam: (lastPage, allPages) => {
    const loadedPagesCount = allPages.reduce(
      (acc, item) => acc + item.products.length,
      0
    );

    return loadedPagesCount < lastPage.totalItems
      ? allPages.length + 1
      : undefined;
  },
  staleTime: 0,
  cacheTime: 0,
  keepPreviousData: false
});

// These are the attributes that can be used to sort the reorder products
export enum ReorderProductSortAttribute {
  NumOrder = 'num_orders',
  QtyPerMonth = 'qty_per_month',
  ProductSpan = 'product_span',
  LastOrder = 'last_order',
  LastQty = 'last_qty',
  Account = 'account',
  AccountId = 'account_id',
  ShipTo = 'ship_to',
  ShipToId = 'ship_to_id',
  ItemNumber = 'item_number',
  ExpectedOrder = 'expected_order',
  ExpectedQty = 'expected_qty',
  DaysOverdue = 'days_overdue',
  Confidence = 'confidence',
  Modified = 'modified',
  LastSellPrice = 'last_sell_price',
  GP = 'gp',
  PriorityLabel = 'priority_label',
  ConfidenceLabel = 'confidence_label'
}

interface CustomerReorderSummary {
  CustomerId?: string;
  ShipId?: string;
  DaysLate?: number;
  Search?: string;
  SalesRepId?: string;
  TerritoryId?: string;
  PriorityLabel?: string;
  ConfidenceLabel?: string;
}

export const customersReorderSummary = (
  params: CustomerReorderSummary = {}
): UseQueryOptions<Summary> => ({
  queryKey: ['customer-reorders-details', params],
  queryFn: ({ signal }) => {
    const controller = new AbortController();

    const promise = axiosHelper({
      endpoint: `/api/1.0/products/reorder/summary`,
      method: AxiosMethods.GET,
      params: cleanParams(params),
      baseURL: process.env.INTELLIGENCE_API_BASE_URL,
      signal: controller.signal
    });

    signal?.addEventListener('abort', () => {
      controller.abort();
    });

    return promise.then(res => res.summary);
  }
});

export const ignoreReorderProducts = () => ({
  mutationFn: ({
    products,
    reason
  }: {
    products: ReorderProducts[];
    reason: string;
  }) => {
    const body = products.reduce(
      (acc, product) => ({
        customerIds: [...acc.customerIds, product.accountId],
        shipToIds: [...acc.shipToIds, product.shipToId],
        productSkus: [...acc.productSkus, product.productKey],
        ignores: [...acc.ignores, true],
        ignoreReasons: [...acc.ignoreReasons, reason]
      }),
      {
        customerIds: [],
        shipToIds: [],
        productSkus: [],
        ignores: [],
        ignoreReasons: []
      }
    );

    return axiosHelper({
      baseURL: process.env.INTELLIGENCE_API_BASE_URL,
      endpoint: `/api/1.0/products/reorder/update`,
      method: AxiosMethods.POST,
      body
    });
  }
});

interface OrderItemsHistory {
  accountId: string;
  addressId: string;
  productId: string;
}

export const orderItemsHistory = () => ({
  mutationFn: (body: OrderItemsHistory) =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: OrdersApi.orderItemsHistory,
      method: AxiosMethods.POST,
      body: { ...body, limit: 10, offset: 0 }
    })
});

type GetThreePMReportQueryParams = {
  searchStr?: string;
  limit: number;
  offset: number;
  filters?: any;
  sort?: any;
};

export const getThreePMReportQuery = (
  params: GetThreePMReportQueryParams
): UseInfiniteQueryOptions<GetThreePMReport> => ({
  queryKey: [
    OrdersQueryKey.THREE_PM_REPORT,
    {
      filters: params.filters,
      searchStr: params.searchStr || '',
      sort: params.sort
    }
  ],
  queryFn: (data: QueryFunctionContext) =>
    axiosHelper({
      baseURL: process.env.REVOPS_API,
      endpoint: OrdersApi.threePMItems,
      method: AxiosMethods.POST,
      body: {
        searchStr: params.searchStr || '',
        limit: params.limit,
        offset: data.pageParam || params.offset,
        filters: params.filters,
        sort: [
          {
            field: params.sort.sortBy,
            direction: params.sort.orderBy
          }
        ]
      }
    }),
  cacheTime: 0,
  staleTime: 0,
  getNextPageParam: (lastPage, allPages) => {
    const total = allPages?.reduce((acc, cur) => acc + cur?.data?.length, 0);
    if (total >= lastPage?.total) return undefined;
    return allPages.length * params?.limit;
  }
});
